/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   migrations.ts
 *     Description     :   TanStack Query hooks for the additive spec-003 migration endpoints —
 *                         search, fetch, create, and source-parameter get/save.
 *     Module          :   006-migration-wizard
 *     Component Type  :   API
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-10-15      Peter Dobler        MigrationSearchFilter.active → active=true (the
 *                                                     console's Active Migrations list); buildSearchQuery
 *                                                     exported for its test
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   @tanstack/react-query @azure/msal-browser
 *     Internal        :   migrationControl msalConfig migration-wizard-types control-plane-types
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     postWithAuth putWithAuth MigrationSearchFilter useMigrationSearch useMigration
 *     useCreateMigration useMigrationParameters useSaveParameters
 *
 * =========================================================================================
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { getAccessToken } from "../auth/token.js";
import { fetchWithAuth } from "./migrationControl.js";
import type {
  MigrationClientOption,
  MigrationConfiguration,
  MigrationRecord,
  MigrationStats,
  SourceParameterSet,
} from "@nextgenmigration/shared/migration-wizard";
import type { ListEnvelope } from "@nextgenmigration/shared/control-plane-types";

const CONTROL_PLANE_URL = (import.meta.env["VITE_CONTROL_PLANE_URL"] as string | undefined) ?? "";

/** Acquires a bearer token (MSAL, or synthetic in bypass) and performs an authenticated JSON request. */
async function mutateWithAuth<T>(url: string, method: "POST" | "PUT", body: unknown): Promise<T> {
  const token = await getAccessToken();

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(await requestFailedMessage(response));
  }

  return response.json() as Promise<T>;
}

/** Build an actionable error message from a failed response — surface the body's `error` when present
 *  (e.g. a 422 from Check Version prepare carries the SQL error), not just the status code. */
export async function requestFailedMessage(response: Response): Promise<string> {
  let detail = "";
  try {
    const body = (await response.json()) as { error?: unknown; message?: unknown };
    detail = typeof body?.error === "string" ? body.error : typeof body?.message === "string" ? body.message : "";
  } catch {
    /* non-JSON error body — fall back to the status */
  }
  return detail
    ? `Migration Control API request failed (${String(response.status)}): ${detail}`
    : `Migration Control API request failed: ${String(response.status)}`;
}

/** Authenticated POST with a JSON body. */
export function postWithAuth<T>(url: string, body: unknown): Promise<T> {
  return mutateWithAuth<T>(url, "POST", body);
}

/** Authenticated PUT with a JSON body. */
export function putWithAuth<T>(url: string, body: unknown): Promise<T> {
  return mutateWithAuth<T>(url, "PUT", body);
}

/** Authenticated DELETE (no body). */
export async function deleteWithAuth<T>(url: string): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Migration Control API request failed: ${String(response.status)}`);
  }
  return response.json() as Promise<T>;
}

/** Roll back a just-created, never-reserved migration (guarded server-side to unreserved rows). */
export function deleteMigration(id: number): Promise<{ deleted: boolean }> {
  return deleteWithAuth<{ deleted: boolean }>(
    `${CONTROL_PLANE_URL}/api/v1/migrations/${String(id)}`,
  );
}

/** Search filter for the migrations list endpoint (all fields optional). */
export interface MigrationSearchFilter {
  npi?: string;
  ticket?: string;
  source?: string;
  tech?: string;
  client?: string;
  /** Exact target-environment filter (DEMO / UAT / PROD / TRAINING). */
  targetEnv?: string;
  /** Free-text search across all displayed fields; comma-separated terms are AND'd. */
  search?: string;
  /** Active migrations only (InUse = 1 AND SignOff = 0, the legacy MainForm list); else every one. */
  active?: boolean;
  page?: number;
  pageSize?: number;
}

/** Builds the query string for the migrations search endpoint, omitting empty values. */
export function buildSearchQuery(filter: MigrationSearchFilter): string {
  const params = new URLSearchParams();
  if (filter.npi) params.set("npi", filter.npi);
  if (filter.ticket) params.set("ticket", filter.ticket);
  if (filter.source) params.set("source", filter.source);
  if (filter.tech) params.set("tech", filter.tech);
  if (filter.client) params.set("client", filter.client);
  if (filter.targetEnv) params.set("targetEnv", filter.targetEnv);
  if (filter.search) params.set("search", filter.search);
  if (filter.active === true) params.set("active", "true");
  if (filter.page !== undefined) params.set("page", String(filter.page));
  if (filter.pageSize !== undefined) params.set("pageSize", String(filter.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** TanStack Query hook: search/list migrations by filter. */
export function useMigrationSearch(
  filter: MigrationSearchFilter,
): ReturnType<typeof useQuery<ListEnvelope<MigrationRecord>>> {
  return useQuery<ListEnvelope<MigrationRecord>>({
    queryKey: ["migrations", "search", filter],
    queryFn: () =>
      fetchWithAuth<ListEnvelope<MigrationRecord>>(
        `${CONTROL_PLANE_URL}/api/v1/migrations${buildSearchQuery(filter)}`,
      ),
  });
}

/**
 * TanStack Query hook: search selectable pharmacies/orgs (PharmacyName / NPI / PMNPI) for the
 * new-migration picker. Empty `q` returns the first page ordered by name.
 */
export function useClientSearch(q: string): ReturnType<typeof useQuery<MigrationClientOption[]>> {
  return useQuery<MigrationClientOption[]>({
    queryKey: ["clients", "search", q],
    queryFn: () =>
      fetchWithAuth<{ items: MigrationClientOption[] }>(
        `${CONTROL_PLANE_URL}/api/v1/clients?q=${encodeURIComponent(q)}`,
      ).then((r) => r.items),
  });
}

/** What the spec-030 resolve probe returns for a pharmacy that resolves. */
export interface ResolvedOrganization {
  organizationId: string;
  organizationName: string;
}

/**
 * Spec 030 FR-005 — probe whether a pharmacy resolves to exactly one active organization.
 *
 * A PROBE, not the gate: creation re-resolves authoritatively before inserting, because the
 * directory can change between the two calls. Its job is to stop the operator at SELECTION rather
 * than after they have filled in the whole form. `retry: false` so a refusal (409) surfaces at once
 * instead of being retried as if it were a transient fault.
 */
export function useOrganizationProbe(clientId: number): ReturnType<typeof useQuery<ResolvedOrganization>> {
  return useQuery<ResolvedOrganization>({
    queryKey: ["clients", clientId, "organization"],
    queryFn: () =>
      fetchWithAuth<ResolvedOrganization>(
        `${CONTROL_PLANE_URL}/api/v1/clients/${String(clientId)}/organization`,
      ),
    enabled: clientId > 0,
    retry: false,
  });
}

/** TanStack Query hook: single migration by id. */
export function useMigration(id: number): ReturnType<typeof useQuery<MigrationRecord>> {
  return useQuery<MigrationRecord>({
    queryKey: ["migrations", id],
    queryFn: () =>
      fetchWithAuth<MigrationRecord>(`${CONTROL_PLANE_URL}/api/v1/migrations/${String(id)}`),
    enabled: id > 0,
  });
}

/**
 * TanStack mutation hook: create (materialize) a migration record from a configuration. The create
 * endpoint returns ONLY the new id `{ id }` — not a full record — so we invalidate and let the full
 * record be (re)fetched via {@link useMigration}; caching the partial `{ id }` as the record would
 * leave consumers with an undefined `stages`/identity.
 */
/**
 * The create response. Spec 030 FR-011 — `warning` is present ONLY when the pharmacy has no prior
 * regular-UAT migration carrying a formulary identity, so its ABSENCE is meaningful (SC-008). It is
 * not an error: the migration was created and is usable.
 */
export interface CreatedMigrationResponse {
  id: number;
  warning?: string;
}

export function useCreateMigration(): UseMutationResult<
  CreatedMigrationResponse,
  Error,
  MigrationConfiguration
> {
  const queryClient = useQueryClient();
  return useMutation<CreatedMigrationResponse, Error, MigrationConfiguration>({
    mutationFn: (config) =>
      postWithAuth<{ id: number }>(`${CONTROL_PLANE_URL}/api/v1/migrations`, config),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["migrations"] });
    },
  });
}

/** TanStack Query hook: aggregate dashboard metrics (counts by status/env, avg runtime per source). */
export function useMigrationStats(): ReturnType<typeof useQuery<MigrationStats>> {
  return useQuery<MigrationStats>({
    queryKey: ["migrations", "stats"],
    queryFn: () => fetchWithAuth<MigrationStats>(`${CONTROL_PLANE_URL}/api/v1/migrations/stats`),
    refetchInterval: 30_000,
  });
}

/** Editable core fields of an existing migration (NPI + reserved env are immutable, so excluded). */
export interface UpdateMigrationInput {
  ticket: string;
  sourceSystem: string;
  sourceEnv?: string;
  targetSystem: string;
  targetEnvironment: string;
  classification: { retail: boolean; compound: boolean };
}

/** TanStack mutation hook: update (PUT) an existing migration's editable core fields. */
export function useUpdateMigration(
  id: number,
): UseMutationResult<MigrationRecord, Error, UpdateMigrationInput> {
  const queryClient = useQueryClient();
  return useMutation<MigrationRecord, Error, UpdateMigrationInput>({
    mutationFn: (input) =>
      putWithAuth<MigrationRecord>(`${CONTROL_PLANE_URL}/api/v1/migrations/${String(id)}`, input),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ["migrations"] });
      queryClient.setQueryData(["migrations", id], record);
    },
  });
}

/** TanStack Query hook: source-parameter values for a migration. */
export function useMigrationParameters(
  id: number,
): ReturnType<typeof useQuery<SourceParameterSet>> {
  return useQuery<SourceParameterSet>({
    queryKey: ["migrations", id, "parameters"],
    queryFn: () =>
      fetchWithAuth<SourceParameterSet>(
        `${CONTROL_PLANE_URL}/api/v1/migrations/${String(id)}/parameters`,
      ),
    enabled: id > 0,
  });
}

/** TanStack mutation hook: persist (PUT) a partial source-parameter set for a migration. */
export function useSaveParameters(
  id: number,
): UseMutationResult<SourceParameterSet, Error, Partial<SourceParameterSet>> {
  const queryClient = useQueryClient();
  return useMutation<SourceParameterSet, Error, Partial<SourceParameterSet>>({
    mutationFn: (params) =>
      putWithAuth<SourceParameterSet>(
        `${CONTROL_PLANE_URL}/api/v1/migrations/${String(id)}/parameters`,
        params,
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData(["migrations", id, "parameters"], updated);
    },
  });
}
